import React from 'react';

const Landing = ({ setView }) => {
  return (
    <main className="flex flex-col min-h-[calc(100vh-3.5rem)] md:min-h-[calc(100vh-4rem)]">
      
      {/* Bio Section — Top Half */}
      <section className="flex-1 flex flex-col items-center justify-center text-center p-6 md:p-12 border-b border-outline-variant/10">
        <div className="max-w-3xl">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-headline font-extrabold tracking-tighter text-on-surface mb-6 uppercase leading-tight">
            NOMIT VERMA // SOFTWARE ENGINEER
          </h1>
          <p className="text-base md:text-lg text-on-surface-variant font-body leading-relaxed max-w-2xl mx-auto">
            ~6 years of experience building high-quality software solutions. Specializing in microservices, API optimizations, and cloud computing.
          </p>
          
          <div className="mt-8 flex flex-wrap justify-center gap-8 md:gap-12">
            <div className="text-left">
              <div className="text-[10px] font-label text-on-surface-variant/60 uppercase tracking-widest mb-1">Current Role</div>
              <div className="text-sm text-on-surface font-bold">SDE-III @ Certa.ai</div>
            </div>
            <div className="text-left">
              <div className="text-[10px] font-label text-on-surface-variant/60 uppercase tracking-widest mb-1">Previously</div>
              <div className="text-sm text-on-surface font-bold">Sr. SDE @ Spinny</div>
            </div>
            <div className="text-left">
              <div className="text-[10px] font-label text-on-surface-variant/60 uppercase tracking-widest mb-1">Location</div>
              <div className="text-sm text-on-surface font-bold">Delhi, IN</div>
            </div>
          </div>
        </div>
      </section>

      {/* Interface Selection — Bottom Half */}
      <section className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        {/* LAUNCH GUI */}
        <div 
          className="relative group cursor-pointer border-b lg:border-b-0 lg:border-r border-outline-variant/10 bg-surface flex flex-col overflow-hidden transition-colors hover:bg-surface-container-low"
          onClick={() => setView('gui')}
        >
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none overflow-hidden">
            <div className="grid grid-cols-6 gap-4 p-8">
              <div className="h-32 bg-on-surface/30 ghost-border"></div>
              <div className="h-32 bg-on-surface/30 ghost-border col-span-2"></div>
              <div className="h-32 bg-on-surface/30 ghost-border"></div>
              <div className="h-32 bg-on-surface/30 ghost-border col-span-2"></div>
              <div className="h-64 bg-on-surface/30 ghost-border col-span-3"></div>
              <div className="h-64 bg-on-surface/30 ghost-border col-span-3"></div>
            </div>
          </div>
          
          <div className="flex-1 flex flex-col justify-center items-center py-12 md:py-16 px-8 md:px-12 relative z-10 transition-transform duration-700 group-hover:scale-[1.02]">
            <div className="mb-4 flex space-x-2 items-center">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="text-[10px] uppercase tracking-widest font-label text-on-surface-variant">Visual Dashboard</span>
            </div>
            
            <h2 className="text-5xl md:text-7xl font-headline font-black tracking-tighter text-on-surface mb-3 text-center">LAUNCH GUI</h2>
            <p className="font-label text-sm text-on-surface-variant max-w-md text-center leading-relaxed">
              Explore my portfolio through an interactive dashboard with projects, skills, and contact form.
            </p>
            
            <div className="mt-8 bg-surface-container-high/80 backdrop-blur border border-outline-variant/20 px-8 py-3 flex items-center space-x-6 rounded">
              <div className="flex flex-col">
                <span className="text-[10px] text-on-surface-variant/60 uppercase font-label">Light Mode</span>
                <span className="text-xs text-on-surface font-bold">STABLE</span>
              </div>
              <div className="h-6 w-[1px] bg-outline-variant/30"></div>
              <div className="flex flex-col">
                <span className="text-[10px] text-on-surface-variant/60 uppercase font-label">Dark Mode</span>
                <span className="text-xs text-on-surface font-bold">NATIVE</span>
              </div>
            </div>
          </div>
          <div className="p-4 md:p-6 border-t border-outline-variant/10 flex justify-between items-center z-10">
            <span className="text-[10px] font-label text-on-surface-variant/40">{'// GRAPHICAL_USER_INTERFACE'}</span>
            <span className="material-symbols-outlined text-on-surface">arrow_forward</span>
          </div>
        </div>

        {/* OPEN TERMINAL */}
        <div 
          className="relative group cursor-pointer bg-surface-container-lowest flex flex-col overflow-hidden transition-colors hover:bg-surface-container-high"
          onClick={() => setView('terminal')}
        >
          <div className="absolute inset-0 p-8 font-mono text-[10px] text-emerald-600/20 dark:text-emerald-500/20 overflow-hidden leading-tight pointer-events-none">
            <div>[SYSTEM] INITIALIZING KERNEL...</div>
            <div>[LOAD] CORE_SERVICES_V4.2</div>
            <div>[AUTH] VERIFYING: NOMIT_VERMA</div>
            <div>[NETWORK] SOCKET 127.0.0.1:8080</div>
            <div>[DB] POSTGRES: HEALTHY</div>
            <div>[CACHE] REDIS: ACTIVE</div>
            <div className="mt-4">Loading portfolio data...</div>
            <div>Parsing experience...</div>
          </div>
          
          <div className="flex-1 flex flex-col justify-center items-center py-12 md:py-16 px-8 md:px-12 relative z-10 transition-transform duration-700 group-hover:scale-[1.02]">
            <div className="mb-4 flex space-x-2 items-center">
              <span className="w-2 h-2 bg-on-surface animate-pulse"></span>
              <span className="text-[10px] uppercase tracking-widest font-label text-on-surface-variant">Terminal Access</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-headline font-black tracking-tighter text-on-surface mb-3 text-center">OPEN TERMINAL</h2>
            <p className="font-label text-sm text-on-surface-variant max-w-md text-center leading-relaxed">
              Navigate my portfolio through a CLI. Type commands to explore projects, experience, and skills.
            </p>
            
            <div className="mt-8 bg-surface-container-lowest border border-outline-variant/20 px-6 py-3 flex items-center min-w-[280px] md:min-w-[300px] rounded">
              <span className="text-emerald-600 dark:text-emerald-500 font-mono mr-3">$</span>
              <span className="text-on-surface font-mono text-sm">ssh visitor@nomit.dev</span>
              <span className="blinking-cursor"></span>
            </div>
          </div>
          <div className="p-4 md:p-6 border-t border-outline-variant/10 flex justify-between items-center z-10">
            <span className="text-[10px] font-label text-on-surface-variant/40">{'// COMMAND_LINE_INTERFACE'}</span>
            <span className="material-symbols-outlined text-on-surface">terminal</span>
          </div>
        </div>
      </section>

      {/* Floating Action Button */}
      <div className="fixed bottom-12 right-6 md:right-12 z-40">
        <a href="mailto:nomitverma1@gmail.com" className="w-12 h-12 md:w-14 md:h-14 bg-primary text-on-primary rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-all duration-200">
          <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>mail</span>
        </a>
      </div>
    </main>
  );
};

export default Landing;
